import m from './module';
import Form from '../model/Form';
import FieldBlock from "../model/FieldBlock";
import {
  getProvisionalIdentifierForType,
  sortItems,
  resetItemsDisplayOrder,
} from '../util';
import { cloneDeep } from 'lodash';

const type = 'Form';
const multiLanguageFields = ['name'];

class FormService {
  constructor(
    $q,
    FieldInstanceService,
    FieldBlockService,
    LogicGroupService,
    ProductService,
    DataSyncService,
    LanguageService,
    LogService,
    RoleVariableService,
    LocationService
  ) {
    "ngInject";
    this.$q = $q;
    this.FieldInstanceService = FieldInstanceService;
    this.FieldBlockService = FieldBlockService;
    this.LogicGroupService = LogicGroupService;
    this.ProductService = ProductService;
    this.DataSyncService = DataSyncService;
    this.LanguageService = LanguageService;
    this.LogService = LogService;
    this.RoleVariableService = RoleVariableService;
    this.LocationService = LocationService;
  }

  create(data) {
    let dataCopy = data || {};
    dataCopy.id = dataCopy.id || getProvisionalIdentifierForType('form');

    // Populate multi-language fields
    dataCopy = this.LanguageService.populateMultiLanguageFields(dataCopy, multiLanguageFields);

    const recapDefinitionId = dataCopy.recapDefinitionId || dataCopy.events_recaps_id || null;

    const items = dataCopy.items || [];
    const itemsPromise = this.$q
      .all(
        items.map(
          item => {
            if (item['items']) {
              return this.$q.when(
                this.FieldBlockService.create({
                  ...item,
                  recapDefinitionId,
                })
                  .catch(err => {
                    this.LogService.warn(`FormService.create() swallowing error creating field block ${item.id} on form ${dataCopy.id}:`, err);
                    return null;
                  })
              );
            }
            return this.$q.when(
              this.FieldInstanceService.create({
                ...item,
                recapDefinitionId,
              })
                .catch(err => {
                  this.LogService.warn(`FormService.create() swallowing error creating field instance ${item.id} on form ${dataCopy.id}:`, err);
                  return null;
                })
            )
          }
        )
      )
      .then(allItemsIncludingFailed => allItemsIncludingFailed.filter(item => !!item))
      .then(items => {
        dataCopy.items = items;
        dataCopy.items = sortItems(items);
      });

    const logicGroups = dataCopy.logic_groups || [];
    const logicGroupsPromise = this.$q
      .all(
        logicGroups.map(lg => this.$q.when(this.LogicGroupService.create(lg)))
      )
      .then(logicGroups => dataCopy.logic_groups = logicGroups);

    const products = dataCopy.products || [];
    const productsPromise = this.$q
      .all(
        products.map(p => this.$q.when(this.ProductService.create(p)))
      )
      .then(products => dataCopy.products = products);

    return this.$q.all([
      itemsPromise,
      logicGroupsPromise,
      productsPromise,
    ])
      .then(() => {
        const form = new Form(dataCopy);

        // Make a copy of the form with references to all fields on it, but remove recursive references
        const formCopy = cloneDeep(form);
        formCopy.items.forEach(item => {
          if (item instanceof FieldBlock) {
            item.items.forEach(fieldInstance => {
              fieldInstance.form = null;
            });
          } else {
            item.form = null;
          }
        });

        // Add the form reference to all the fields
        form.items.forEach(item => {
          if (item instanceof FieldBlock) {
            item.items.forEach(fieldInstance => {
              fieldInstance.form = formCopy;
            });
          } else {
            item.form = formCopy;
          }
        })

        return this.DataSyncService.setObjectById(type, dataCopy.id, form);
      })
      .catch(err => {
        this.LogService.error(`FormService.create() error creating form ${dataCopy.id} for recap definition ${recapDefinitionId}:`, err);
        return this.$q.reject(err);
      });
  }

  serialize(form) {
    resetItemsDisplayOrder(form.items);
    let data = {
      id: form.id,
      form_type: form.form_type,
      collapse_product_form: form.collapse_product_form,
      items: form.items.map(item => {
        if (item instanceof FieldBlock) {
          return this.FieldBlockService.serialize(item);
        }
        return this.FieldInstanceService.serialize(item);
      }),
      logic_groups: form.logic_groups.map(group => this.LogicGroupService.serialize(group)),
      name: form.name,
      name_l10n: form.name_l10n,
      display_order: form.display_order,
      products: form.products.map(product => this.ProductService.serialize(product, form.id)),
      location_variable_ids: form.location_variables.map(lv => lv.id),
      role_variables: form.role_variables.map(rv => this.RoleVariableService.serialize({...rv, events_custom_forms_id: form.id})),
      locations: form.locations.map(loc => this.LocationService.serialize({...loc, events_custom_forms_id: form.id})),
      merchandising_activities_ids: (form.merchandising_activities || [])
        .map(activity => Number(activity && activity.id))
        .filter(id => Number.isFinite(id) && id > 0),
    };
    data = this.LanguageService.populateMultiLanguageFields(data, multiLanguageFields);
    return data;
  }

  getById(id) {
    return this.DataSyncService.getLocalObjectById(type, id);
  }
}

export default FormService;

m.service('FormService', FormService);
